import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus, Clock, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useBusinessId } from '@/hooks/useBusinessId';
import { appointmentsApi } from '@/api/appointments';
import { formatTime } from '@/utils/time';
import { addDays, formatDateLabel, todayStr } from '@/utils/date';
import { StatusBadge } from '../components/status-badge';

function toDisplayStatus(stateCode: string): 'in-progress' | 'upcoming' | 'completed' | 'cancelled' | 'expired' {
  switch (stateCode) {
    case 'COMPLETED': return 'completed';
    case 'CANCELLED': return 'cancelled';
    case 'NO_SHOW': return 'cancelled';
    case 'EXPIRED': return 'expired';
    default: return 'upcoming';
  }
}

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);
}

export function Appointments() {
  const businessId = useBusinessId();
  const [selectedDate, setSelectedDate] = useState(todayStr);

  const { data: appointments = [], isLoading } = useQuery({
    queryKey: ['appointments', 'list', businessId, selectedDate],
    queryFn: () => appointmentsApi.search(businessId, {
      appointmentDate: selectedDate,
      period: 'day',
    }),
    enabled: !!businessId,
    staleTime: 30_000,
  });

  const sorted = [...appointments].sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''));

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
      <div className="flex-1 overflow-auto" style={{ paddingTop: '16px', paddingLeft: '14px', paddingRight: '14px', paddingBottom: '96px' }}>

        {/* Date picker + new button */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedDate(d => addDays(d, -1))}
              className="p-1.5 rounded-lg transition-colors hover:bg-black/5"
              style={{ color: 'var(--muted-fg)' }}
            >
              <ChevronLeft size={18} />
            </button>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', color: 'var(--dark-teal)', minWidth: '100px', textAlign: 'center' }}>
              {formatDateLabel(selectedDate)}
            </span>
            <button
              onClick={() => setSelectedDate(d => addDays(d, 1))}
              className="p-1.5 rounded-lg transition-colors hover:bg-black/5"
              style={{ color: 'var(--muted-fg)' }}
            >
              <ChevronRight size={18} />
            </button>
          </div>
          <Link
            to="/appointment/new"
            className="flex items-center gap-1.5 transition-colors hover:opacity-90"
            style={{ backgroundColor: 'var(--terracotta)', color: 'var(--off-white)', padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 500 }}
          >
            <Plus size={14} strokeWidth={2.5} />
            Nueva
          </Link>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--muted-fg)' }} />
          </div>
        ) : sorted.length === 0 ? (
          <div
            className="rounded-xl border flex items-center justify-center"
            style={{ backgroundColor: 'var(--off-white)', borderColor: 'var(--border-color)', padding: 'var(--space-4)', minHeight: '200px' }}
          >
            <div className="text-center">
              <div style={{ fontSize: '15px', color: 'var(--muted-fg)', marginBottom: '12px' }}>
                Sin citas para este día
              </div>
              <Link
                to="/appointment/new"
                className="inline-flex items-center gap-1.5 transition-colors hover:opacity-90"
                style={{ backgroundColor: 'var(--terracotta)', color: 'var(--off-white)', padding: '8px 20px', borderRadius: '999px', fontSize: '14px', fontWeight: 500 }}
              >
                <Plus size={16} />
                Agendar cita
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {sorted.map((apt) => (
              <Link key={apt.id} to={`/appointment-view/${apt.id}`} className="block">
                <div
                  className="rounded-2xl transition-all hover:shadow-md"
                  style={{
                    backgroundColor: 'var(--off-white)',
                    border: '1px solid var(--border-color)',
                    boxShadow: '0 2px 8px rgba(36, 62, 61, 0.06)',
                    padding: '16px 18px',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ width: '40px', height: '40px', backgroundColor: 'var(--peach)', color: 'var(--dark-teal)', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600 }}
                    >
                      {getInitials(apt.customerName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate min-w-0" style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', color: 'var(--dark-teal)' }}>
                          {apt.customerName}
                        </span>
                        <div className="flex-shrink-0">
                          <StatusBadge status={toDisplayStatus(apt.stateCode)} size="sm" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 min-w-0" style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>
                        <span className="flex-shrink-0">{formatTime(apt.startTime)}</span>
                        <span className="flex-shrink-0" style={{ opacity: 0.4 }}>·</span>
                        <span className="truncate min-w-0">{apt.serviceName}</span>
                        {apt.duration && (
                          <>
                            <span style={{ opacity: 0.4 }}>·</span>
                            <span className="flex items-center gap-0.5">
                              <Clock size={10} />
                              {apt.duration}m
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
